import React from "react";
import { Grid, TextField, IconButton, Stack, Chip, Button, Box } from "@mui/material";
import { Autocomplete } from "@mui/material";
import { getIn } from "formik";
import { DropDownIcon } from "../../../pages/admin/BulkUpdateSettings/AgentSelector";
import OZCheckbox from "../../OZCheckbox/OZCheckbox";

/**
 * @param {Object} props
 * @param {Array} props.customTagList - List of available custom tags
 * @param {Object} props.formik - Formik instance with the form values and methods
 * @param {string} props.fieldArrayName - Name of the field array in formik (default: 'agentTag')
 * @param {boolean} props.readOnly - Whether the component is in read-only mode
 */
const CustomTagSelector = ({
    customTagList,
    formik,
    fieldArrayName = "agentTag",
    readOnly = false
}) => {
    // Get the current values
    const tagValues = formik.values[fieldArrayName] || [];

    // State to track input values for each autocomplete
    const [inputValues, setInputValues] = React.useState({});

    const handleAddTag = () => {
        const updatedTags = [...tagValues, { tagName: "", tagValue: [] }];
        formik.setFieldValue(fieldArrayName, updatedTags);
    };

    const handleRemoveTag = (index) => {
        const updatedTags = tagValues.filter((_, i) => i !== index);
        formik.setFieldValue(fieldArrayName, updatedTags);

        // Clean up input values state
        setInputValues(prev => {
            const newInputValues = { ...prev };
            delete newInputValues[index];

            // Reindex remaining input values
            const reindexed = {};
            Object.keys(newInputValues).forEach(key => {
                const keyIndex = parseInt(key);
                if (keyIndex > index) {
                    reindexed[keyIndex - 1] = newInputValues[key];
                } else {
                    reindexed[key] = newInputValues[key];
                }
            });

            return reindexed;
        });
    };

    const handleTagChange = (index, newValue) => {
        // Update tag
        formik.setFieldValue(`${fieldArrayName}[${index}].tagName`, newValue ? newValue.tagName : "");
        formik.setFieldValue(`${fieldArrayName}[${index}].tagValue`, []);
    };

    const handleTagValueChange = (index, newValue) => {
        formik.setFieldValue(`${fieldArrayName}[${index}].tagValue`, newValue);
    };

    const handleSelectAll = (index, e) => {
        try {
            if (e) {
                e.preventDefault();
                e.stopPropagation();
            }

            const tagId = tagValues[index].tagName;
            if (!tagId) return;

            // Get all available options for this tag
            const allOptions = customTagList.find(item => item.tagName === tagId)?.tagValue || [];

            // Get current input value for filtering
            const currentInput = inputValues[index] || '';

            // Filter options based on current search input
            const filteredOptions = currentInput
                ? allOptions.filter(option => option.toLowerCase().includes(currentInput.toLowerCase()))
                : allOptions;

            const currentValues = tagValues[index].tagValue || [];

            // Merge current values with filtered options (avoid duplicates)
            const newValues = [...new Set([...currentValues, ...filteredOptions])];

            formik.setFieldValue(`${fieldArrayName}[${index}].tagValue`, newValues);

            // Preserve the input value after the update
            if (currentInput) {
                setTimeout(() => {
                    setInputValues(prev => ({
                        ...prev,
                        [index]: currentInput
                    }));
                }, 0);
            }
        } catch (e) {
            const tagId = tagValues[index].tagName;
            if (!tagId) return;
            const availableOptions = customTagList.find(item => item.tagName === tagId)?.tagValue || [];
            formik.setFieldValue(`${fieldArrayName}[${index}].tagValue`, [...availableOptions]);
            console.log("e", e);
        }
    };

    const handleClearAll = (index, e) => {
        try {
            if (e) {
                e.preventDefault();
                e.stopPropagation();
            }

            const tagId = tagValues[index].tagName;
            if (!tagId) return;

            // Get all available options for this tag
            const allOptions = customTagList.find(item => item.tagName === tagId)?.tagValue || [];

            // Get current input value for filtering
            const currentInput = inputValues[index] || '';

            // Filter options based on current search input
            const filteredOptions = currentInput
                ? allOptions.filter(option => option.toLowerCase().includes(currentInput.toLowerCase()))
                : allOptions;

            const currentValues = tagValues[index].tagValue || [];

            // Remove only the filtered options from current values
            const newValues = currentInput
                ? currentValues.filter(value => !filteredOptions.includes(value))
                : []; // If no search input, clear all (original behavior)

            formik.setFieldValue(`${fieldArrayName}[${index}].tagValue`, newValues);

            // Preserve the input value after the update
            if (currentInput) {
                setTimeout(() => {
                    setInputValues(prev => ({
                        ...prev,
                        [index]: currentInput
                    }));
                }, 0);
            }
        } catch (e) {
            formik.setFieldValue(`${fieldArrayName}[${index}].tagValue`, []);
            console.log("e", e);
        }
    };

    function getOptionsBasedOnSelectedValue(indexToSkip) {
        const selectedTagIds = tagValues.map((item, index) => index !== indexToSkip ? item.tagName : '-1');
        const fullOptions = [...customTagList];
        const newOptions = [];
        if (selectedTagIds && Array.isArray(selectedTagIds) && selectedTagIds.length > 0) {
            fullOptions.forEach(item => {
                if (item && item.tagName && !selectedTagIds.includes(item.tagName)) {
                    newOptions.push(item);
                }
            });
        }
        return newOptions;
    }

    function getErrorsByNameAndIndex(name, index) {
        // Use getIn for safer property access
        const error = getIn(formik.errors, `${fieldArrayName}[${index}].${name}`);
        const touched = getIn(formik.touched, `${fieldArrayName}[${index}].${name}`);

        return touched && error ? error : null;
    }

    console.log("formik.errors", formik.errors);
    return (
        <Grid container spacing={3}>
            {tagValues.map((tag, index) => {
                const hasTagName = tag.tagName && tag.tagName !== "";
                const tagValueError = getErrorsByNameAndIndex("tagValue", index);
                const tagNameError = getErrorsByNameAndIndex("tagName", index);

                return (
                    <React.Fragment key={index}>
                        <Grid item xs={5}>
                            <Autocomplete
                                options={getOptionsBasedOnSelectedValue(index)}
                                getOptionLabel={(option) => option.tagName || ""}
                                popupIcon={<DropDownIcon />}
                                value={customTagList.find(item => item.tagName === tag.tagName) || null}
                                onChange={(e, newValue) => handleTagChange(index, newValue)}
                                isOptionEqualToValue={(option, value) => option.tagName === value.tagName}
                                disabled={readOnly}
                                componentsProps={{
                                    paper: { sx: { border: "1px solid #D6D6D7", marginTop: "6px", borderRadius: "8px" } }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Select Tag"
                                        fullWidth
                                        size="small"
                                        onBlur={() => {
                                            formik.setFieldTouched(`${fieldArrayName}[${index}].tagName`, true);
                                            formik.setFieldTouched(`${fieldArrayName}[${index}].tagValue`, true);
                                        }}
                                        error={Boolean(tagNameError)}
                                        helperText={tagNameError}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                fontSize: "14px",
                                                color: "#3D4C5E",
                                                borderRadius: '8px',
                                                minHeight: "44px"
                                            },
                                            minHeight: "44px"
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={5}>
                            <Autocomplete
                                id={`customTagValue-${fieldArrayName}-${index}`}
                                multiple
                                options={customTagList.find(item => item.tagName === tag.tagName)?.tagValue || []}
                                componentsProps={{
                                    paper: {
                                        sx: {
                                            border: "1px solid #D6D6D7",
                                            marginTop: "6px",
                                            borderRadius: "8px",
                                        }
                                    }
                                }}
                                limitTags={2}
                                value={tag.tagValue || []}
                                inputValue={inputValues[index] || ''}
                                popupIcon={<DropDownIcon />}
                                onChange={(e, newValue) => handleTagValueChange(index, newValue)}
                                onInputChange={(event, newInputValue) => {
                                    setInputValues(prev => ({
                                        ...prev,
                                        [index]: newInputValue
                                    }));
                                }}
                                disableCloseOnSelect={true}
                                renderTags={(value) => {
                                    if (value && Array.isArray(value) && value.length > 0) {
                                        return value.map((item) => (
                                            <Chip label={item || ''} onDelete={null} size="small" sx={{ ml: 0.4, mt: 0.2 }} />
                                        ))
                                    }
                                    return null;
                                }}
                                renderOption={(props, option, { selected }) => {
                                    // Special handling for the action buttons row
                                    if (option === "ACTION_BUTTONS_ROW") {
                                        return (
                                            <li {...props}
                                                style={{
                                                    padding: 0,
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    borderBottom: '1px solid #E5E5E5',
                                                    height: 'auto !important',
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                onMouseDown={(e) => e.preventDefault()}
                                            >
                                                <Button
                                                    sx={{
                                                        textTransform: 'none',
                                                        flex: 1,
                                                        justifyContent: 'center',
                                                        fontFamily: 'Roboto',
                                                        fontWeight: 400,
                                                        color: '#3D8BF8',
                                                        borderRight: '1px solid #E5E5E5',
                                                        minWidth: 'auto',
                                                        borderRadius: 0
                                                    }}
                                                    onClick={(e) => handleSelectAll(index, e)}
                                                    onMouseDown={(e) => e.preventDefault()}
                                                >
                                                    Select All
                                                </Button>
                                                <Button
                                                    style={{
                                                        textTransform: 'none',
                                                        flex: 1,
                                                        justifyContent: 'center',
                                                        fontFamily: 'Roboto',
                                                        fontWeight: 400,
                                                        color: '#3D8BF8',
                                                        minWidth: 'auto',
                                                        borderRadius: 0
                                                    }}
                                                    onClick={(e) => handleClearAll(index, e)}
                                                    onMouseDown={(e) => e.preventDefault()}
                                                >
                                                    Clear All
                                                </Button>
                                            </li>
                                        );
                                    }

                                    return (
                                        <li {...props} style={{ backgroundColor: "#FFFFFF !important" }}>
                                            <OZCheckbox checked={selected} />
                                            <Box sx={{
                                                pb: 1,
                                                pl: 1
                                            }}>
                                                {option}
                                            </Box>
                                        </li>
                                    );
                                }}
                                filterOptions={(options, params) => {
                                    // First filter options based on input
                                    const filtered = params.inputValue
                                        ? options.filter(option =>
                                            option.toLowerCase().includes(params.inputValue.toLowerCase()))
                                        : options;

                                    // Insert the action buttons row at the beginning
                                    if (filtered.length > 0 && tag.tagName) {
                                        filtered.unshift("ACTION_BUTTONS_ROW");
                                    }

                                    return filtered;
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder={hasTagName ? "Select Values *" : "Select Values"}
                                        onBlur={() => formik.setFieldTouched(`${fieldArrayName}[${index}].tagValue`, true)}
                                        error={Boolean(tagValueError)}
                                        helperText={tagValueError}
                                        fullWidth
                                        size="small"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                fontSize: "14px",
                                                color: "#3D4C5E",
                                                borderRadius: '8px',
                                                minHeight: "44px"
                                            },
                                            minHeight: "44px"
                                        }}
                                    />
                                )}
                                disabled={!tag.tagName || readOnly}
                                sx={{
                                    "& .MuiAutocomplete-listbox": {
                                        "& li": {
                                            backgroundColor: "#FFFFFF !important"
                                        },
                                        "& li:hover": {
                                            backgroundColor: "#FFFFFF !important"
                                        }
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <Stack direction="row" spacing={1} mt={index > 0 && !readOnly ? 0.3 : .75}>
                                {index > 0 && !readOnly && (
                                    <IconButton
                                        onClick={() => handleRemoveTag(index)}
                                        size="small"
                                        sx={{ color: '#F44336' }}
                                    >
                                        <img src={`${process.env.PUBLIC_URL}/icons/deleteIconGrey.svg`} alt="delete" />
                                    </IconButton>
                                )}
                                {index === tagValues.length - 1 && !readOnly && (
                                    <IconButton
                                        onClick={handleAddTag}
                                        size="small"
                                        sx={{ color: '#3D8BF8' }}
                                    >
                                        <img src={`${process.env.PUBLIC_URL}/icons/anotherlevel.svg`} alt="add" />
                                    </IconButton>
                                )}
                            </Stack>
                        </Grid>
                    </React.Fragment>
                )
            })}
        </Grid >
    );
};

export default CustomTagSelector; 